import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, Filter, Edit, Trash2, Plus, Download, RefreshCw, 
  CheckCircle, XCircle, AlertCircle, Clock, Calendar, 
  TrendingUp, DollarSign, MousePointerClick, Percent, Tag
} from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import dealService from '../../services/dealService';
import DealFormModal from './components/DealFormModal';
import DealMetricsModal from './components/DealMetricsModal';
import BulkActionsModal from './components/BulkActionsModal';
import Icon from '../../components/AppIcon';


const AdminDealManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [deals, setDeals] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState('all');
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showDealModal, setShowDealModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [selectedDealForMetrics, setSelectedDealForMetrics] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && !authLoading) {
      loadDeals();
    }
  }, [user, authLoading]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, selectedStatus, selectedPartner, deals]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const data = await dealService?.getAllDeals();
      setDeals(data || []);
      setFilteredDeals(data || []);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...deals];

    // Search filter
    if (searchQuery) {
      filtered = filtered?.filter(deal =>
        deal?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        deal?.affiliatePartner?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        deal?.products?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Status filter
    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(deal => deal?.status === selectedStatus);
    }

    // Partner filter
    if (selectedPartner !== 'all') {
      filtered = filtered?.filter(deal => deal?.affiliatePartner === selectedPartner);
    }

    setFilteredDeals(filtered);
  };

  const handleSelectDeal = (dealId) => {
    setSelectedDeals(prev =>
      prev?.includes(dealId)
        ? prev?.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDeals?.length === filteredDeals?.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(filteredDeals?.map(d => d?.id));
    }
  };

  const handleCreateDeal = () => {
    setEditingDeal(null);
    setShowDealModal(true);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setShowDealModal(true);
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;

    try {
      await dealService?.deleteDeal(dealId);
      await loadDeals();
    } catch (error) {
      console.error('Error deleting deal:', error);
      alert('Failed to delete deal: ' + error?.message);
    }
  };

  const handleViewMetrics = (deal) => {
    setSelectedDealForMetrics(deal);
    setShowMetricsModal(true);
  };

  const handleBulkActions = () => {
    if (selectedDeals?.length === 0) {
      alert('Please select at least one deal');
      return;
    }
    setShowBulkModal(true);
  };

  const handleExportData = () => {
    const csvData = filteredDeals?.map(d => ({
      Title: d?.title,
      Product: d?.products?.name || 'N/A',
      Partner: d?.affiliatePartner,
      Discount: `${d?.discountValue}${d?.discountType === 'percentage' ? '%' : '$'}`,
      Price: `$${d?.dealPrice}`,
      Status: d?.status,
      Clicks: d?.clickCount,
      Conversions: d?.conversionCount,
      Revenue: `$${d?.revenueGenerated}`,
      Expiration: new Date(d?.expirationDate)?.toLocaleDateString()
    }));

    const csv = [
      Object.keys(csvData?.[0])?.join(','),
      ...csvData?.map(row => Object.values(row)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deals-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Clock },
      expired: { bg: 'bg-gray-100', text: 'text-gray-800', icon: XCircle },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: AlertCircle }
    };
    
    const badge = badges?.[status] || badges?.active;
    const Icon = badge?.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getExpirationStatus = (expirationDate) => {
    const now = new Date();
    const expiry = new Date(expirationDate);
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return <span className="text-red-600 text-sm font-medium">Expired</span>;
    } else if (daysUntilExpiry <= 2) {
      return <span className="text-orange-600 text-sm font-medium">Expires in {daysUntilExpiry}d</span>;
    } else {
      return <span className="text-gray-600 text-sm">{daysUntilExpiry} days left</span>;
    }
  };

  const uniquePartners = [...new Set(deals?.map(d => d?.affiliatePartner)?.filter(Boolean))]?.sort();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Deal Management</h1>
              <p className="text-gray-600 mt-2">
                Create, edit, and manage time-sensitive deals with price tracking and performance analytics
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={loadDeals}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={handleExportData}
                variant="outline"
                className="flex items-center gap-2"
                disabled={filteredDeals?.length === 0}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Button
                onClick={handleCreateDeal}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Deal
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Deals</p>
                <p className="text-2xl font-bold text-gray-900">
                  {deals?.filter(d => d?.status === 'active')?.length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900">
                  {deals?.reduce((sum, d) => sum + (d?.clickCount || 0), 0)?.toLocaleString()}
                </p>
              </div>
              <MousePointerClick className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {deals?.reduce((sum, d) => sum + (d?.conversionCount || 0), 0)?.toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-indigo-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${deals?.reduce((sum, d) => sum + (d?.revenueGenerated || 0), 0)?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </h2>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="ghost"
              size="sm"
            >
              {showFilters ? 'Hide' : 'Show'} Advanced Filters
            </Button>
          </div>

          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by deal title, product, or affiliate partner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e?.target?.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="expired">Expired</option>
                    <option value="paused">Paused</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Affiliate Partner
                  </label>
                  <Select
                    value={selectedPartner}
                    onChange={(e) => setSelectedPartner(e?.target?.value)}
                  >
                    <option value="all">All Partners</option>
                    {uniquePartners?.map(partner => (
                      <option key={partner} value={partner}>{partner}</option>
                    ))}
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedStatus('all');
                      setSelectedPartner('all');
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedDeals?.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-indigo-900">
                <span className="font-semibold">{selectedDeals?.length}</span> deal(s) selected
              </p>
              <Button
                onClick={handleBulkActions}
                variant="default"
                size="sm"
              >
                Bulk Actions
              </Button>
            </div>
          </div>
        )}

        {/* Deals Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredDeals?.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No deals found</p>
              <Button
                onClick={handleCreateDeal}
                variant="outline"
                className="mt-4"
              >
                Create Your First Deal
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <Checkbox
                        checked={selectedDeals?.length === filteredDeals?.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deal Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Partner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeals?.map((deal) => (
                    <tr key={deal?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Checkbox
                          checked={selectedDeals?.includes(deal?.id)}
                          onChange={() => handleSelectDeal(deal?.id)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {deal?.title}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {deal?.products?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {deal?.products?.brand}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm font-medium text-indigo-600">
                          <Percent className="w-3 h-3" />
                          {deal?.discountValue}{deal?.discountType === 'percentage' ? '%' : '$'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          ${deal?.dealPrice}
                        </div>
                        <div className="text-xs text-gray-500 line-through">
                          ${deal?.originalPrice}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {deal?.affiliatePartner}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(deal?.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm text-gray-900">
                              {new Date(deal?.expirationDate)?.toLocaleDateString()}
                            </div>
                            {getExpirationStatus(deal?.expirationDate)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs space-y-1">
                          <div className="flex items-center gap-1 text-gray-600">
                            <MousePointerClick className="w-3 h-3" />
                            {deal?.clickCount || 0} clicks
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <TrendingUp className="w-3 h-3" />
                            {deal?.conversionCount || 0} conversions
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => handleViewMetrics(deal)}
                            variant="ghost"
                            size="sm"
                            title="View Metrics"
                          >
                            <TrendingUp className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleEditDeal(deal)}
                            variant="ghost"
                            size="sm"
                            title="Edit Deal"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeleteDeal(deal?.id)}
                            variant="ghost"
                            size="sm"
                            title="Delete Deal"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showDealModal && (
        <DealFormModal
          deal={editingDeal}
          onClose={() => {
            setShowDealModal(false);
            setEditingDeal(null);
          }}
          onSuccess={() => {
            setShowDealModal(false);
            setEditingDeal(null);
            loadDeals();
          }}
        />
      )}

      {showMetricsModal && selectedDealForMetrics && (
        <DealMetricsModal
          deal={selectedDealForMetrics}
          onClose={() => {
            setShowMetricsModal(false);
            setSelectedDealForMetrics(null);
          }}
        />
      )}

      {showBulkModal && (
        <BulkActionsModal
          selectedDeals={selectedDeals}
          onClose={() => setShowBulkModal(false)}
          onSuccess={() => {
            setShowBulkModal(false);
            setSelectedDeals([]);
            loadDeals();
          }}
        />
      )}
    </div>
  );
};

export default AdminDealManagement;